import {
  BaseAutoTracker,
  IAutoTrackerConfig,
  ICapturedEvent,
} from './BaseAutoTracker';
import { SelectorUtils } from './utils/SelectorUtils';
import { EventManager } from './utils/EventManager';
import { GomarkePixelConfig, PixelResponse } from '@/types';
import { InitiateCheckoutTracker } from '@/conversion/tracks/initiateCheckout';
import type { TrackConversionFn } from '../engine';
import type { IInitiateCheckoutData } from '@/conversion/tracks/initiateCheckout';

/**
 * Configuração específica para autoInitiateCheckout
 */
export interface IAutoInitiateCheckoutConfig extends IAutoTrackerConfig {
  /** Se true, chama preventDefault() no submit do formulário (default false — não bloquear checkout). */
  preventSubmit?: boolean;
  extractCartData?: boolean;
  cartDataSelectors?: {
    items?: string;
    total?: string;
    currency?: string;
    itemCount?: string;
  };
  customCartExtractor?: (element: Element) => {
    items?: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>;
    total?: number;
    currency?: string;
  };
  trackOnNavigation?: boolean;
  navigationSelectors?: string[];
}

/**
 * AutoTracker para eventos de início de checkout
 */
export class AutoInitiateCheckoutTracker extends BaseAutoTracker {
  private checkoutTracker: InitiateCheckoutTracker;
  private eventManager: EventManager;
  private autoConfig?: IAutoInitiateCheckoutConfig;
  private trackConversion?: TrackConversionFn;
  /** Listener global (capture) para submit de qualquer form quando selectorType=automatic. */
  private globalFormSubmitListener?: (event: Event) => void;
  private navigationListeners: Map<
    string,
    (event: Event) => Promise<void> | void
  > = new Map();

  constructor(
    config: GomarkePixelConfig,
    sessionId: string,
    version: string,
    visitorId: string = '',
    trackConversion?: TrackConversionFn
  ) {
    super(config, sessionId, version, visitorId);
    this.trackConversion = trackConversion;
    this.checkoutTracker = new InitiateCheckoutTracker(
      config,
      sessionId,
      visitorId,
      version
    );
    this.eventManager = new EventManager({
      debounceMs: 500,
      throttleMs: 2000,
      enableLogging: config.debug,
    });
  }

  private trackCheckout(
    payload: IInitiateCheckoutData
  ): Promise<PixelResponse> {
    if (this.trackConversion) {
      return this.trackConversion('initiateCheckout', payload);
    }
    return this.checkoutTracker.track(payload);
  }

  /**
   * Inicializa o autoTracker
   */
  public async initialize(
    autoConfig: IAutoInitiateCheckoutConfig
  ): Promise<void> {
    if (!autoConfig.isEnabled) {
      this.log('AutoInitiateCheckout desabilitado');
      return;
    }

    this.autoConfig = autoConfig;
    this.log(
      `Inicializando AutoInitiateCheckout com seletor: ${autoConfig.selector}`
    );

    // Aguarda DOM estar pronto
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () =>
        this.setupListeners()
      );
    } else {
      await this.setupListeners();
    }

    // Configura tracking de navegação se habilitado
    if (autoConfig.trackOnNavigation) {
      this.setupNavigationTracking();
    }

    // Observa mudanças no DOM para elementos dinâmicos
    this.observeDOM(() => this.setupListeners());

    this.isInitialized = true;
  }

  /**
   * Configura os listeners
   */
  private async setupListeners(): Promise<void> {
    if (!this.autoConfig) return;

    // Limpa listeners existentes
    this.clearListeners();
    this.clearGlobalFormSubmitListener();

    try {
      // Regra de negócio nova:
      // Quando o "initiateCheckoutDetection" vier como AUTOMATIC (selectorType=automatic),
      // detectar submit de QUALQUER <form> na página e disparar initiateCheckout.
      // Mantém o comportamento anterior para os demais selectorTypes (text/attribute/etc).
      if (this.autoConfig.selectorType === 'automatic') {
        this.setupAutomaticFormSubmitTracking();
        // Garante que continuará observando DOM (clearListeners desconecta o observer)
        this.observeDOM(() => this.setupListeners());
        return;
      }

      const elements = await SelectorUtils.findElementsWithRetry(
        this.autoConfig.selector,
        this.autoConfig.selectorType,
        3,
        1000
      );

      this.log(
        `Encontrados ${elements.length} elementos para InitiateCheckout`
      );

      elements.forEach(element => {
        // Converte Node para Element
        const htmlElement = element as Element;

        // Listener para clique
        this.addListenerWithTiming(
          htmlElement,
          'click',
          this.handleCheckoutClick.bind(this),
          this.autoConfig!
        );

        // Listener para teclado
        this.addListenerWithTiming(
          htmlElement,
          'keydown',
          this.handleKeyboardCheckout.bind(this),
          this.autoConfig!
        );

        // Listener para submit (se for formulário)
        if (htmlElement.tagName.toLowerCase() === 'form') {
          this.addListenerWithTiming(
            htmlElement,
            'submit',
            this.handleFormSubmit.bind(this),
            this.autoConfig!
          );
        }
      });
    } catch (error) {
      this.log(`Erro ao configurar listeners: ${error}`);
    }

    // Garante que continuará observando DOM (clearListeners desconecta o observer)
    this.observeDOM(() => this.setupListeners());
  }

  /**
   * Detecta submit de qualquer <form> (capture) quando selectorType=automatic.
   */
  private setupAutomaticFormSubmitTracking(): void {
    if (!this.autoConfig) return;
    if (this.globalFormSubmitListener) return; // já configurado

    const cfg: IAutoInitiateCheckoutConfig = {
      ...this.autoConfig,
      selector: 'form',
      selectorType: 'automatic',
    };

    this.globalFormSubmitListener = (event: Event) => {
      const target = event.target;
      let formEl: Element | null = null;

      if (target instanceof HTMLFormElement) {
        formEl = target;
      } else if (target instanceof Element) {
        formEl = target.closest('form');
      }

      if (!formEl) return;

      // Usa o pipeline padrão (capturedEvent + retry) da BaseAutoTracker
      void this.executeCallback(
        formEl,
        event,
        this.handleFormSubmit.bind(this),
        cfg
      );
    };

    // Capture=true para pegar submits mesmo se algo parar bubbling.
    document.addEventListener('submit', this.globalFormSubmitListener, true);
    this.log('Listener global de submit (automatic) configurado');
  }

  private clearGlobalFormSubmitListener(): void {
    if (!this.globalFormSubmitListener) return;
    document.removeEventListener('submit', this.globalFormSubmitListener, true);
    this.globalFormSubmitListener = undefined;
  }

  /**
   * Configura tracking de navegação
   */
  private setupNavigationTracking(): void {
    if (
      !this.autoConfig?.trackOnNavigation ||
      !this.autoConfig.navigationSelectors
    )
      return;

    this.autoConfig.navigationSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);

      elements.forEach(element => {
        // Converte Node para Element
        const htmlElement = element as Element;
        const listener = (event: Event) => {
          this.handleNavigationCheckout(event);
        };

        this.navigationListeners.set(selector, listener);
        htmlElement.addEventListener('click', listener);
      });
    });
  }

  /**
   * Manipula clique em checkout
   */
  private async handleCheckoutClick(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('Checkout detectado via clique', capturedEvent);

    const cartData = this.extractCartData(capturedEvent.element);

    return this.trackCheckout({
      value: cartData.total,
      currency: cartData.currency || 'BRL',
      items: cartData.items,
    });
  }

  /**
   * Manipula teclas de checkout
   */
  private async handleKeyboardCheckout(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    const keyboardEvent = capturedEvent.event as KeyboardEvent;

    if (keyboardEvent.key !== 'Enter' && keyboardEvent.key !== ' ') {
      return Promise.resolve({
        success: false,
        error: 'Tecla não suportada',
        timestamp: Date.now(),
      });
    }

    this.log('Checkout detectado via teclado', capturedEvent);
    return this.handleCheckoutClick(capturedEvent);
  }

  /**
   * Manipula submit de formulário
   */
  private async handleFormSubmit(
    capturedEvent: ICapturedEvent
  ): Promise<PixelResponse> {
    this.log('Checkout detectado via submit de formulário', capturedEvent);

    // Previne submit apenas se configurado (default false — pixel não deve bloquear checkout).
    const formEvent = capturedEvent.event as Event & {
      preventDefault?: () => void;
    };
    if (this.autoConfig?.preventSubmit === true && formEvent.preventDefault) {
      formEvent.preventDefault();
    }

    const cartData = this.extractCartData(capturedEvent.element);

    return this.trackCheckout({
      value: cartData.total,
      currency: cartData.currency || 'BRL',
      items: cartData.items,
    });
  }

  /**
   * Manipula navegação para checkout
   */
  private async handleNavigationCheckout(event: Event): Promise<void> {
    this.log('Navegação para checkout detectada', event);

    // Extrai dados do carrinho da página atual
    const cartData = this.extractCartDataFromPage();

    await this.trackCheckout({
      value: cartData.total,
      currency: cartData.currency || 'BRL',
      items: cartData.items,
    });
  }

  /**
   * Extrai dados do carrinho do elemento
   */
  private extractCartData(element: Element): {
    items?: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>;
    total?: number;
    currency?: string;
  } {
    if (!this.autoConfig) {
      return {};
    }

    const data: Record<string, unknown> = {};

    // Extrai dados usando seletores configurados
    if (this.autoConfig.cartDataSelectors) {
      const selectors = this.autoConfig.cartDataSelectors;

      if (selectors.total) {
        data.total = element.getAttribute(selectors.total);
      }

      if (selectors.currency) {
        data.currency = element.getAttribute(selectors.currency);
      }

      if (selectors.items) {
        data.items = element.getAttribute(selectors.items);
      }
    }

    if (this.autoConfig?.customCartExtractor) {
      const customData = this.autoConfig.customCartExtractor(element);
      Object.assign(data, customData);
    }

    return data;
  }

  /**
   * Extrai dados do carrinho da página inteira
   */
  private extractCartDataFromPage(): {
    items?: Array<{
      item_id: string;
      item_name: string;
      category?: string;
      quantity: number;
      price: number;
    }>;
    total?: number;
    currency?: string;
  } {
    // Implementação básica - pode ser expandida
    const cartItems = document.querySelectorAll('[data-cart-item]');
    const items = Array.from(cartItems).map(item => this.parseCartItem(item));

    const totalElement = document.querySelector('[data-cart-total]');
    const total = totalElement
      ? this.parsePrice(totalElement.textContent)
      : undefined;

    return {
      items,
      total,
      currency: 'BRL',
    };
  }

  /**
   * Converte elemento em item do carrinho
   */
  private parseCartItem(element: Element): {
    item_id: string;
    item_name: string;
    category?: string;
    quantity: number;
    price: number;
  } {
    return {
      item_id:
        element.getAttribute('data-item-id') || this.generateItemId(element),
      item_name:
        element.getAttribute('data-item-name') ||
        element.textContent?.trim() ||
        'Item',
      category: element.getAttribute('data-item-category') || undefined,
      quantity: parseInt(element.getAttribute('data-item-quantity') || '1'),
      price: this.parsePrice(element.getAttribute('data-item-price')) || 0,
    };
  }

  /**
   * Gera ID único para o item
   */
  private generateItemId(element: Element): string {
    const position = SelectorUtils.getElementPosition(element);
    return `checkout_item_${Date.now()}_${Math.floor(position.top)}_${Math.floor(position.left)}`;
  }

  /**
   * Converte string de preço para número
   */
  private parsePrice(priceText?: string | null): number | undefined {
    if (!priceText) return undefined;

    const cleanPrice = priceText.toString().replace(/[^\d,.-]/g, '');
    const normalizedPrice = cleanPrice.replace(',', '.');
    const price = parseFloat(normalizedPrice);

    return isNaN(price) ? undefined : price;
  }

  /**
   * Para o autoTracker
   */
  public stop(): void {
    this.clearListeners();
    this.clearGlobalFormSubmitListener();
    this.clearNavigationListeners();
    this.eventManager.clear();
    this.isInitialized = false;
    this.log('AutoInitiateCheckout parado');
  }

  /**
   * Limpa listeners de navegação
   */
  private clearNavigationListeners(): void {
    this.navigationListeners.forEach((listener, selector) => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        // Converte Node para Element
        const htmlElement = element as Element;
        htmlElement.removeEventListener('click', listener);
      });
    });
    this.navigationListeners.clear();
  }

  /**
   * Reconstrói os listeners
   */
  public async rebuild(): Promise<void> {
    if (this.autoConfig) {
      await this.setupListeners();
      if (this.autoConfig.trackOnNavigation) {
        this.setupNavigationTracking();
      }
    }
  }

  /**
   * Obtém estatísticas específicas
   */
  public getCheckoutStats(): {
    baseStats: unknown;
    eventStats: unknown;
    navigationListeners: number;
  } {
    return {
      baseStats: {},
      eventStats: this.eventManager.getStats(),
      navigationListeners: this.navigationListeners.size,
    };
  }

  /**
   * Log de eventos
   */
  private log(message: string, data?: unknown): void {
    if (this.baseConfig.debug) {
      console.log(`[AutoInitiateCheckout] ${message}`, data);
    }
  }
}

// Função de conveniência para compatibilidade
export const autoInitiateCheckout = (
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  _config: IAutoInitiateCheckoutConfig
): void => {
  console.warn(
    'autoInitiateCheckout: Use a classe AutoInitiateCheckoutTracker diretamente'
  );
};
